import clsx from 'clsx'
import flush from 'just-flush'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FilterSearchToolbar from '@/components-latest/ui/OrdersPage/FilterSearchToolbar'
import { HistoryResults } from '@/components-latest/ui/OrdersPage/HistoryOrders/HistoryResults'
import { SearchResults } from '@/components-latest/ui/OrdersPage/HistoryOrders/SearchResults'
import {
  selectHasFilters,
  selectHasSearch,
  selectOrderFilters,
  selectOrderSearchParams
} from '@/ducks/orders/selectors/orders.selectors'
import {
  useLazyFetchOrdersHistoryListQuery,
  useLazySearchOrdersHistoryQuery
} from '@/ducks/orders/services/orders.api'
import { resetOrdersStateToInitial } from '@/ducks/orders/slices/orders.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './HistoryOrders.module.scss'

const HistoryOrders = () => {
  const dispatch = useDispatch()

  const filters = useSelector(selectOrderFilters)
  const searchParams = useSelector(selectOrderSearchParams)
  const hasSearch = useSelector(selectHasSearch)
  const hasFilters = useSelector(selectHasFilters)
  const storeId = useSelector(selectStoreId)

  const [
    fetchOrdersHistory,
    { data: _orders, isFetching: isOrdersLoading, error }
  ] = useLazyFetchOrdersHistoryListQuery()

  useEffect(() => {
    if (error) {
      console.error('Error fetching orders history:', error)
    }
  }, [error])

  const orders = _orders?.orders

  const getOrders = useCallback(() => {
    if (!storeId) return
    fetchOrdersHistory({
      skip: 0,
      limit: 73,
      store_id: storeId
    })
  }, [fetchOrdersHistory, storeId])

  useEffect(() => {
    getOrders()
  }, [getOrders, storeId])

  useEffect(() => {
    return () => {
      dispatch(resetOrdersStateToInitial())
    }
  }, [dispatch])

  const [searchHistory, searchQuery] = useLazySearchOrdersHistoryQuery()

  useEffect(
    function searchWhenFiltersChange() {
      const { statuses, ...restFilters } = filters
      const strippedParams = flush({
        ...restFilters,
        category: searchParams.category,
        search: searchParams.value,
        ...(statuses && statuses.length > 0 ? { statuses } : {})
      })

      if (storeId && Object.values(strippedParams).length > 0) {
        searchHistory({
          ...strippedParams,
          skip: 0,
          limit: 73,
          store_id: storeId,
          ...(searchParams?.value ? {} : { isFilterOnly: true })
        })
      }
    },
    [filters, searchHistory, searchParams, storeId]
  )

  const shouldShowSearchResult = hasSearch || hasFilters
  const shouldBeFaded =
    !shouldShowSearchResult && orders && orders.length > 0 && isOrdersLoading

  return (
    <div className={Styles.historyOrders}>
      <FilterSearchToolbar />
      <div className={clsx(Styles.orders, { [Styles.faded]: shouldBeFaded })}>
        {shouldShowSearchResult ? (
          <SearchResults
            orders={searchQuery.data}
            isLoading={searchQuery.isLoading}
          />
        ) : (
          <HistoryResults orders={orders} isLoading={isOrdersLoading} />
        )}
      </div>
    </div>
  )
}

export default HistoryOrders
